import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../common/utils';

// ---------------------------------------------------------------------------
// useFetchData — with refetch support
// ---------------------------------------------------------------------------

export function useFetchData<T>(url: string, initialData: T) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: tick triggers a refetch when incremented by refetch()
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((d) => { if (active) { setData(d); setLoading(false); } })
      .catch((e) => { if (active) { setError(e instanceof Error ? e : new Error(String(e))); setLoading(false); } });

    return () => { active = false; };
  }, [url, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { data, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// useMutation — POST/PUT/PATCH/DELETE with CSRF
// ---------------------------------------------------------------------------

type MutationState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'success' }
  | { phase: 'error'; message: string };

export function useMutation(url: string, method: string = 'POST') {
  const [state, setState] = useState<MutationState>({ phase: 'idle' });

  const mutate = useCallback(async (body?: unknown): Promise<Response | null> => {
    setState({ phase: 'loading' });
    try {
      const res = await fetchOrThrow(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          [CSRF_HEADER_NAME]: await getCSRFToken(),
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      setState({ phase: 'success' });
      return res;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setState({ phase: 'error', message: msg });
      return null;
    }
  }, [url, method]);

  const reset = useCallback(() => setState({ phase: 'idle' }), []);

  return { mutate, state, reset };
}

// ---------------------------------------------------------------------------
// useCountUp — animates a number from 0 to target
// ---------------------------------------------------------------------------

export function useCountUp(target: number, duration = 600): number {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const startVal = useRef(0);
  const raf = useRef<number>(0);

  useEffect(() => {
    startVal.current = 0;
    startTime.current = null;

    const ease = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

    const step = (ts: number) => {
      if (!startTime.current) startTime.current = ts;
      const elapsed = ts - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(startVal.current + (target - startVal.current) * ease(progress)));
      if (progress < 1) raf.current = requestAnimationFrame(step);
    };

    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
}

// ---------------------------------------------------------------------------
// useToast
// ---------------------------------------------------------------------------

export type Toast = { id: string; message: string; variant: 'success' | 'error' | 'info'; durationMs?: number };

let _listeners: Array<(t: Toast) => void> = [];

export function showToast(message: string, variant: Toast['variant'] = 'success', durationMs?: number) {
  const id = Math.random().toString(36).slice(2);
  const toast: Toast = { id, message, variant, durationMs };
  _listeners.forEach((fn) => { fn(toast); });
}

export function useToastBus(): { toasts: Toast[]; dismiss: (id: string) => void } {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const fn = (t: Toast) => {
      setToasts((prev) => [...prev, t]);
      setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), t.durationMs ?? 4000);
    };
    _listeners.push(fn);
    return () => { _listeners = _listeners.filter((l) => l !== fn); };
  }, []);

  const dismiss = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  return { toasts, dismiss };
}

// ---------------------------------------------------------------------------
// useLocalStorage
// ---------------------------------------------------------------------------

export function useLocalStorage<T>(key: string, defaultValue: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const set = useCallback((v: T) => {
    setValue(v);
    try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ }
  }, [key]);

  return [value, set];
}
